import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { ChevronDown } from "lucide-react";
import { createPortal } from "react-dom";

export type SearchComboboxOption = {
  id: string;
  label: string;
  description?: string;
};

type SearchComboboxProps = {
  label: string;
  placeholder: string;
  query: string;
  value: string;
  options: SearchComboboxOption[];
  open: boolean;
  loading?: boolean;
  disabled?: boolean;
  onQueryChange: (query: string) => void;
  onOpenChange: (open: boolean) => void;
  onSelect: (option: SearchComboboxOption) => void;
};

export function SearchCombobox({
  label,
  placeholder,
  query,
  value,
  options,
  open,
  loading = false,
  disabled = false,
  onQueryChange,
  onOpenChange,
  onSelect,
}: SearchComboboxProps) {
  const listboxId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [position, setPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
    maxHeight: 0,
  });

  useLayoutEffect(() => {
    if (!open) return;
    function positionList() {
      const rect = inputRef.current?.getBoundingClientRect();
      if (!rect) return;
      const edge = 8;
      const gap = 6;
      const width = Math.min(rect.width, window.innerWidth - edge * 2);
      const height = Math.min(listRef.current?.scrollHeight ?? 288, 288);
      const spaceBelow = Math.max(
        0,
        window.innerHeight - rect.bottom - gap - edge,
      );
      const spaceAbove = Math.max(0, rect.top - gap - edge);
      const placeAbove = spaceBelow < height && spaceAbove > spaceBelow;
      const maxHeight = Math.min(height, placeAbove ? spaceAbove : spaceBelow);
      const top = placeAbove
        ? Math.max(edge, rect.top - gap - maxHeight)
        : rect.bottom + gap;
      const left = Math.min(
        Math.max(edge, rect.left),
        window.innerWidth - edge - width,
      );
      setPosition({ top, left, width, maxHeight });
    }
    positionList();
    window.addEventListener("resize", positionList);
    window.addEventListener("scroll", positionList, true);
    return () => {
      window.removeEventListener("resize", positionList);
      window.removeEventListener("scroll", positionList, true);
    };
  }, [loading, open, options.length]);

  useEffect(() => {
    if (!open) return;
    setActiveIndex(options.length ? 0 : -1);
  }, [open, options.length]);

  useEffect(() => {
    if (!open) return;
    function closeOutside(event: PointerEvent) {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (
        !inputRef.current?.closest(".replacement-combobox")?.contains(target) &&
        !listRef.current?.contains(target)
      ) {
        onOpenChange(false);
      }
    }
    document.addEventListener("pointerdown", closeOutside);
    return () => document.removeEventListener("pointerdown", closeOutside);
  }, [onOpenChange, open]);

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Tab" && open) {
      onOpenChange(false);
      return;
    }
    if (event.key === "Escape" && open) {
      event.preventDefault();
      event.stopPropagation();
      onOpenChange(false);
      return;
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!open) {
        onOpenChange(true);
        return;
      }
      const direction = event.key === "ArrowDown" ? 1 : -1;
      setActiveIndex((current) => {
        if (!options.length) return -1;
        return (current + direction + options.length) % options.length;
      });
      return;
    }
    if (event.key === "Enter" && open && activeIndex >= 0) {
      event.preventDefault();
      const option = options[activeIndex];
      if (option) {
        onSelect(option);
        onOpenChange(false);
      }
    }
  }

  return (
    <label className="replacement-field replacement-combobox">
      <span>{label}</span>
      <span className="replacement-combobox-trigger">
        <input
          ref={inputRef}
          role="combobox"
          aria-autocomplete="list"
          aria-controls={listboxId}
          aria-expanded={open}
          aria-activedescendant={
            open && activeIndex >= 0
              ? `${listboxId}-option-${activeIndex}`
              : undefined
          }
          aria-label={label}
          autoComplete="off"
          placeholder={placeholder}
          value={query}
          disabled={disabled}
          onFocus={() => onOpenChange(true)}
          onClick={() => onOpenChange(true)}
          onChange={(event) => onQueryChange(event.target.value)}
          onKeyDown={handleKeyDown}
        />
        <ChevronDown
          className="replacement-combobox-chevron"
          size={16}
          aria-hidden="true"
        />
      </span>
      {open &&
        createPortal(
          <div
            ref={listRef}
            id={listboxId}
            className="replacement-combobox-list"
            role="listbox"
            aria-label={`Resultados para ${label.toLowerCase()}`}
            style={{
              top: position.top,
              left: position.left,
              width: position.width,
              maxHeight: position.maxHeight,
            }}
          >
            {loading ? (
              <p className="replacement-combobox-status">Buscando...</p>
            ) : options.length ? (
              options.map((option, index) => (
                <div
                  key={option.id}
                  id={`${listboxId}-option-${index}`}
                  role="option"
                  aria-selected={value === option.id}
                  data-active={activeIndex === index || undefined}
                  className="replacement-combobox-option"
                  onPointerMove={() => setActiveIndex(index)}
                  onPointerDown={(event) => {
                    event.preventDefault();
                    onSelect(option);
                    onOpenChange(false);
                  }}
                >
                  <strong>{option.label}</strong>
                  {option.description ? (
                    <small>{option.description}</small>
                  ) : null}
                </div>
              ))
            ) : (
              <p className="replacement-combobox-status">
                Nenhum resultado encontrado.
              </p>
            )}
          </div>,
          document.body,
        )}
    </label>
  );
}
